// src/v3/InfoModal/index.tsx
import { cx as cx5 } from "class-variance-authority";

// src/v4/Modal/index.tsx
import {
  cloneElement as cloneElement3
} from "react";

// src/v4/Modal/ModalDialog.tsx
import { cx } from "class-variance-authority";
import { cloneElement, useRef } from "react";
import { useDialog } from "react-aria";
import { jsx } from "react/jsx-runtime";
function ModalDialog({
  children,
  className,
  ...props
}) {
  const ref = useRef(null);
  const { dialogProps, titleProps } = useDialog(props, ref);
  return /* @__PURE__ */ jsx("div", {
    ...dialogProps,
    className: cx("ui-w-full ui-overflow-y-auto !ui-outline-none", className),
    "data-cy": "modal-v4-dialog",
    ref,
    children: cloneElement(children({ titleProps }))
  });
}

// src/v4/Modal/ModalTrigger.tsx
import { AnimatePresence } from "framer-motion";
import {
  cloneElement as cloneElement2,
  useRef as useRef3
} from "react";
import { useButton, useOverlayTrigger } from "react-aria";
import {
  useOverlayTriggerState
} from "react-stately";

// src/v4/Modal/ModalWrapper.tsx
import { cx as cx4 } from "class-variance-authority";
import { motion, useReducedMotion } from "framer-motion";
import { useRef as useRef2 } from "react";
import {
  Overlay,
  useModalOverlay
} from "react-aria";

// src/v4/Modal/hooks/useDrawerControls.ts
import {
  useAnimate,
  useDragControls,
  useMotionValue
} from "framer-motion";
function useDrawerControls(state, animated, duration) {
  const [scope, animate] = useAnimate();
  const opacity = useMotionValue(0);
  const y = useMotionValue(0);
  const controls = useDragControls();
  const handleClose = async () => {
    if (animated) {
      void animate(opacity, [1, 0], { duration, ease: "easeInOut" });
      const OVERSHOOT = 100;
      const yStart = typeof y.get() === "number" ? y.get() : 0;
      const drawerOffset = scope.current.getBoundingClientRect().top;
      const yTarget = yStart + (window.innerHeight + OVERSHOOT - drawerOffset);
      await animate(
        scope.current,
        {
          y: [yStart, yTarget]
        },
        {
          duration,
          ease: "easeInOut"
        }
      );
    }
    state.close();
  };
  return {
    controls,
    handleClose,
    opacity,
    scope,
    y
  };
}

// src/v4/Modal/ModalCloseButton.tsx
import { cx as cx3 } from "class-variance-authority";

// src/react-intl/FormattedMessage.tsx
import { FormattedMessage as ReactIntlFormattedMessage } from "react-intl";

// src/v3/BodyLink/index.tsx
import { forwardRef, useContext } from "react";
import { cx as cx2 } from "class-variance-authority";

// src/contexts/ComponentProvider.tsx
import { createContext, useMemo } from "react";
import { jsx as jsx2 } from "react/jsx-runtime";
var Context = createContext(null);
function ComponentProvider({
  children,
  ImageComponent,
  LinkComponent
}) {
  const contextValue = useMemo(
    () => ({ ImageComponent, LinkComponent }),
    [ImageComponent, LinkComponent]
  );
  return /* @__PURE__ */ jsx2(Context.Provider, {
    value: contextValue,
    children
  });
}
ComponentProvider.displayName = "ComponentProvider";

// src/v3/BodyLink/index.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
var BodyLink = forwardRef(
  function BodyLink2({
    children,
    className,
    color = "sandstone-500",
    external = false,
    href = "",
    onClick,
    ...rest
  }, ref) {
    const context = useContext(Context);
    const LinkComponent = external || !(context == null ? void 0 : context.LinkComponent) ? "a" : context.LinkComponent;
    return /* @__PURE__ */ jsx3(LinkComponent, {
      "data-cy": "body_link",
      ...rest,
      className: cx2(`ui-underline ui-text-${color}`, className),
      href,
      onClick: (evt) => onClick == null ? void 0 : onClick(evt),
      ref,
      children
    });
  }
);
BodyLink.displayName = "BodyLink";

// src/react-intl/htmlFormatting.tsx
import { jsx as jsx4 } from "react/jsx-runtime";
var htmlFormatting = {
  br: /* @__PURE__ */ jsx4("br", {}),
  w: /* @__PURE__ */ jsx4("wbr", {}),
  r: String.fromCharCode(174),
  tm: String.fromCharCode(8482),
  break: function breakTag() {
    return /* @__PURE__ */ jsx4("br", {});
  },
  strong: function strong(chunks) {
    return /* @__PURE__ */ jsx4("strong", {
      children: chunks
    });
  },
  b: function b(chunks) {
    return /* @__PURE__ */ jsx4("strong", {
      children: chunks
    });
  },
  em: function em(chunks) {
    return /* @__PURE__ */ jsx4("em", {
      children: chunks
    });
  },
  i: function i(chunks) {
    return /* @__PURE__ */ jsx4("em", {
      children: chunks
    });
  },
  u: function u(chunks) {
    return /* @__PURE__ */ jsx4("span", {
      className: "ui-underline",
      children: chunks
    });
  },
  sup: function sup(chunks) {
    return /* @__PURE__ */ jsx4("sup", {
      className: "ui-font-serif ui-italic ui-text-blue-200 ui-font-semibold ui-tracking-wider",
      children: chunks
    });
  },
  li: function li(chunks) {
    return /* @__PURE__ */ jsx4("li", {
      children: chunks
    });
  },
  ExternalLink: function ExternalLink(href) {
    return /* @__PURE__ */ jsx4(BodyLink, {
      href,
      rel: "noreferrer",
      target: "_blank",
      children: href
    });
  },
  EmailLink: function EmailLink(email) {
    return /* @__PURE__ */ jsx4(BodyLink, {
      href: `mailto:${email}`,
      children: email
    });
  }
};

// src/react-intl/stringFormatting.ts
var stringFormatting = Object.entries(
  htmlFormatting
).reduce((obj, [key, value]) => {
  const type = typeof value;
  if (type === "string") {
    return { ...obj, [key]: value };
  } else if (type === "function") {
    return { ...obj, [key]: (chunks) => chunks };
  }
  return { ...obj, [key]: "" };
}, {});

// src/react-intl/getLocaleFormatting.tsx
var getLocaleFormatting = (options = {}) => {
  const { stripHtml } = options;
  if (stripHtml) {
    return stringFormatting;
  }
  return htmlFormatting;
};
getLocaleFormatting.displayName = "getLocaleFormatting";
var getLocaleFormatting_default = getLocaleFormatting;

// src/react-intl/FormattedMessage.tsx
import { jsx as jsx5 } from "react/jsx-runtime";
function FormattedMessage({
  id,
  values: optionalValues,
  ...props
}) {
  const localeFormatting = getLocaleFormatting_default();
  const values = {
    ...localeFormatting,
    ...optionalValues
  };
  return /* @__PURE__ */ jsx5(ReactIntlFormattedMessage, {
    id,
    values,
    ...props
  });
}
function t(id, values) {
  return /* @__PURE__ */ jsx5(FormattedMessage, {
    id,
    values
  });
}
FormattedMessage.displayName = "FormattedMessage";

// src/v4/Modal/ModalCloseButton.tsx
import { Fragment, jsx as jsx6, jsxs } from "react/jsx-runtime";
function ModalCloseButton({
  controls,
  dragHandleClassName,
  state,
  variant
}) {
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [
      /* @__PURE__ */ jsxs("button", {
        className: cx3(
          "ui-flex ui-w-full ui-cursor-grab ui-touch-none ui-items-center ui-justify-center ui-rounded-t-[20px] ui-p-5 active:ui-cursor-grabbing active:ui-bg-sandstone-400 md:ui-hidden",
          dragHandleClassName
        ),
        "data-cy": "modal-drag-handle",
        onPointerDown: (e) => {
          controls.start(e);
        },
        type: "button",
        children: [
          /* @__PURE__ */ jsx6("span", {
            className: "ui-sr-only",
            children: t("close")
          }),
          /* @__PURE__ */ jsx6("span", {
            className: "ui-block ui-h-2 ui-w-14 ui-cursor-grab ui-touch-none ui-rounded-full ui-bg-[#d6caba] active:ui-cursor-grabbing"
          })
        ]
      }),
      /* @__PURE__ */ jsx6("div", {
        className: cx3("ui-hidden md:ui-block md:ui-absolute md:ui-z-20", {
          "md:ui-top-4 md:ui-right-6": variant === "small",
          "md:ui-top-8 md:ui-right-8": variant === "large" || variant === "extra-large"
        }),
        "data-cy": "modal-close-button-container",
        children: /* @__PURE__ */ jsxs("button", {
          className: "ui-relative ui-h-8 ui-w-8 ui-rounded-full ui-text-gray-400 hover:ui-text-gray-500 ui-bg-inherit",
          "data-cy": "modal-close-button",
          onClick: state.close,
          type: "button",
          children: [
            /* @__PURE__ */ jsx6("span", {
              className: "ui-sr-only",
              children: t("close")
            }),
            /* @__PURE__ */ jsx6("span", {
              className: "ui-block ui-absolute ui-inset-0 ui-rounded-full ui-border-[1.5px] ui-border-current"
            }),
            /* @__PURE__ */ jsx6("span", {
              className: "ui-block ui-absolute ui-top-1/2 ui-left-1/2 ui-h-[1.5px] ui-w-5 -ui-translate-x-1/2 -ui-translate-y-1/2 ui-rotate-45 ui-transform ui-rounded-full ui-bg-current"
            }),
            /* @__PURE__ */ jsx6("span", {
              className: "ui-block ui-absolute ui-top-1/2 ui-left-1/2 ui-h-[1.5px] ui-w-5 -ui-translate-x-1/2 -ui-translate-y-1/2 -ui-rotate-45 ui-transform ui-rounded-full ui-bg-current"
            })
          ]
        })
      })
    ]
  });
}

// src/v4/Modal/ModalWrapper.tsx
import { jsx as jsx7, jsxs as jsxs2 } from "react/jsx-runtime";
function ModalWrapper({
  animated,
  animationDuration,
  backdropClassName,
  blur,
  children,
  className,
  dragHandleClassName,
  state,
  variant,
  ...props
}) {
  const ref = useRef2(null);
  const reduceMotion = useReducedMotion() || !animated;
  const { modalProps, underlayProps } = useModalOverlay(props, state, ref);
  const { controls, handleClose, scope, y } = useDrawerControls(
    state,
    !reduceMotion,
    animationDuration
  );
  const backdropDuration = animationDuration + 0.05;
  return /* @__PURE__ */ jsx7(Overlay, {
    children: /* @__PURE__ */ jsx7(motion.div, {
      animate: { opacity: 1 },
      className: cx4(
        "ui-fixed ui-inset-0 ui-z-200 ui-bg-black/60",
        {
          "ui-backdrop-blur-[10px]": blur
        },
        backdropClassName
      ),
      exit: { opacity: 0 },
      initial: { opacity: 0 },
      transition: {
        duration: backdropDuration,
        ease: "easeInOut"
      },
      children: /* @__PURE__ */ jsx7("div", {
        ...underlayProps,
        className: "ui-size-full ui-flex ui-justify-center ui-items-center",
        "data-cy": "modal-v4-underlay",
        children: /* @__PURE__ */ jsx7(motion.div, {
          animate: { y: 0 },
          className: cx4(
            "ui-relative ui-z-10 ui-w-full ui-mt-auto md:ui-mt-0",
            {
              "md:ui-w-[85vw] md:ui-max-w-[840px] lg:ui-w-[75vw] lg:ui-max-w-none xl:ui-w-[65vw]": variant === "extra-large",
              "md:ui-w-[85vw] md:ui-max-w-[840px] lg:ui-w-[75vw] xl:ui-w-[65vw]": variant === "large",
              "md:ui-max-w-[375px]": variant === "small"
            }
          ),
          drag: "y",
          dragConstraints: { top: 0, bottom: 0 },
          dragControls: controls,
          dragElastic: { top: 0.15, bottom: 0.85 },
          dragListener: false,
          exit: { y: reduceMotion ? 0 : 100 },
          initial: { y: reduceMotion ? 0 : 250 },
          onDragEnd: () => {
            if (y.get() > 100) {
              void handleClose();
            }
          },
          ref: scope,
          style: { y },
          transition: {
            duration: animationDuration,
            ease: "easeInOut"
          },
          children: /* @__PURE__ */ jsxs2("div", {
            ...modalProps,
            className: cx4(
              "ui-w-full ui-max-h-[80dvh] ui-flex ui-flex-col ui-flex-1 ui-rounded-t-[20px] ui-bg-sandstone-200 ui-shadow-[0_200px_0_0] ui-shadow-sandstone-200 ui-px-6 ui-pb-6",
              "md:ui-p-8 md:ui-overflow-hidden md:ui-rounded-[20px] md:ui-shadow-none",
              {
                "md:ui-pt-16": variant === "large",
                "md:ui-pt-10": variant === "small"
              },
              className
            ),
            "data-cy": "modal-v4",
            children: [
              /* @__PURE__ */ jsx7(ModalCloseButton, {
                controls,
                dragHandleClassName,
                state,
                variant
              }),
              children
            ]
          })
        })
      })
    })
  });
}

// src/v4/Modal/ModalTrigger.tsx
import { Fragment as Fragment2, jsx as jsx8, jsxs as jsxs3 } from "react/jsx-runtime";
function ModalTrigger({
  triggerButton,
  children,
  onButtonClick,
  wrapperProps,
  ...props
}) {
  const buttonRef = useRef3(null);
  const overlayTriggerState = useOverlayTriggerState(props);
  const { triggerProps, overlayProps } = useOverlayTrigger(
    { type: "dialog" },
    overlayTriggerState
  );
  const { buttonProps } = useButton(triggerProps, buttonRef);
  const handleButtonClick = (event) => {
    var _a;
    (_a = buttonProps.onClick) == null ? void 0 : _a.call(buttonProps, event);
    onButtonClick == null ? void 0 : onButtonClick(event);
  };
  return /* @__PURE__ */ jsxs3(Fragment2, {
    children: [
      triggerButton({
        ...buttonProps,
        onClick: handleButtonClick,
        ref: buttonRef
      }),
      /* @__PURE__ */ jsx8(AnimatePresence, {
        children: overlayTriggerState.isOpen ? /* @__PURE__ */ jsx8(ModalWrapper, {
          ...wrapperProps,
          state: overlayTriggerState,
          children: cloneElement2(children({ overlayTriggerState }), overlayProps)
        }) : null
      })
    ]
  });
}

// src/v4/Modal/index.tsx
import { jsx as jsx9 } from "react/jsx-runtime";
function Modal({
  animated = true,
  animationDuration = 0.25,
  backdropClassName,
  blur = true,
  children,
  className,
  defaultOpen,
  dialogClassName,
  dragHandleClassName,
  isOpen,
  onOpenChange,
  onTriggerButtonClick,
  triggerButton,
  variant = "small"
}) {
  return /* @__PURE__ */ jsx9(ModalTrigger, {
    defaultOpen,
    isOpen,
    onButtonClick: onTriggerButtonClick,
    onOpenChange,
    triggerButton,
    wrapperProps: {
      animated,
      animationDuration,
      backdropClassName,
      blur,
      className,
      dragHandleClassName,
      variant
    },
    children: ({ overlayTriggerState }) => /* @__PURE__ */ jsx9(ModalDialog, {
      className: dialogClassName,
      children: ({ titleProps }) => cloneElement3(
        children({
          overlayTriggerState,
          titleProps
        })
      )
    })
  });
}

// src/icons/InfoIcon.tsx
import { jsx as jsx10, jsxs as jsxs4 } from "react/jsx-runtime";
function InfoIcon({
  title,
  titleId,
  ...props
}) {
  return /* @__PURE__ */ jsxs4("svg", {
    "aria-hidden": "true",
    "aria-labelledby": titleId,
    fill: "none",
    height: 9,
    viewBox: "0 0 4 9",
    width: 4,
    xmlns: "http://www.w3.org/2000/svg",
    ...props,
    children: [
      title ? /* @__PURE__ */ jsx10("title", {
        id: titleId,
        children: title
      }) : null,
      /* @__PURE__ */ jsx10("path", {
        d: "M1.33333 0.833293C1.33333 0.465103 1.63181 0.166626 2 0.166626C2.36819 0.166626 2.66667 0.465103 2.66667 0.833293C2.66667 1.20148 2.36819 1.49996 2 1.49996C1.63181 1.49996 1.33333 1.20148 1.33333 0.833293Z",
        fill: "currentColor"
      }),
      /* @__PURE__ */ jsx10("path", {
        d: "M0 3.49996C0 3.13177 0.298477 2.83329 0.666667 2.83329H2C2.36819 2.83329 2.66667 3.13177 2.66667 3.49996V7.49996H3.33333C3.70152 7.49996 4 7.79844 4 8.16663C4 8.53482 3.70152 8.83329 3.33333 8.83329H0.666667C0.298477 8.83329 0 8.53482 0 8.16663C0 7.79844 0.298477 7.49996 0.666667 7.49996H1.33333V4.16663H0.666667C0.298477 4.16663 0 3.86815 0 3.49996Z",
        fill: "currentColor"
      })
    ]
  });
}

// src/v3/Typography/index.tsx
import { cva } from "class-variance-authority";
import { jsx as jsx11 } from "react/jsx-runtime";
var typography = cva("", {
  variants: {
    variant: {
      h1: "ui-text-[2.5rem] ui-tracking-tight md:ui-text-[3.75rem] lg:ui-text-[5rem]",
      h2: "ui-text-[2.25rem] ui-tracking-normal md:ui-text-[3rem] lg:ui-text-[4.25rem] lg:ui-tracking-[-2px]",
      h3: "ui-text-[2rem] ui-tracking-normal md:ui-text-[2.5rem] lg:ui-text-[3.5rem]",
      h4: "ui-text-[1.75rem] ui-tracking-normal md:ui-text-[2rem] lg:ui-text-[2.5rem]",
      h5: "ui-text-[1.25rem] ui-tracking-normal lg:ui-leading-10",
      h6: "ui-text-[1.25rem] ui-tracking-normal",
      "body-large": "ui-text-[1.125rem] ui-tracking-normal",
      "title-large": "ui-text-[4.25rem] md:ui-text-7xl lg:ui-text-8xl xl:ui-text-[7.5rem]",
      "title-small": "ui-text-[4.25rem] md:ui-text-7xl lg:ui-text-[5rem]",
      base: "ui-text-base ui-tracking-normal",
      "body-small": "ui-text-[0.875rem] ui-tracking-normal",
      "eyebrow-large": "ui-text-base",
      "eyebrow-small": "ui-text-[0.75rem]",
      unset: ""
    },
    align: {
      left: "ui-text-left",
      center: "ui-text-center",
      right: "ui-text-right",
      justify: "ui-text-justify",
      centerMdLeft: "ui-text-center md:ui-text-left",
      centerLgLeft: "ui-text-center lg:ui-text-left"
    },
    color: {
      light: "ui-text-sandstone-200",
      dark: "ui-text-sandstone-500",
      disabled: "ui-text-gray-300",
      error: "ui-text-red-600",
      gray: "ui-text-gray-400",
      success: "ui-text-green-600",
      inherit: "ui-text-inherit",
      unset: ""
    },
    font: {
      sans: "ui-font-sans",
      serif: "ui-font-serif",
      "serif-italic": "ui-font-serif ui-italic"
    },
    height: {
      none: "ui-leading-none",
      tight: "ui-leading-tight",
      snug: "ui-leading-snug",
      normal: "ui-leading-normal",
      relaxed: "ui-leading-relaxed",
      loose: "ui-leading-loose",
      heading: "ui-leading-heading"
    },
    weight: {
      thin: "ui-font-thin",
      extralight: "ui-font-extralight",
      light: "ui-font-light",
      normal: "ui-font-normal",
      medium: "ui-font-medium",
      semibold: "ui-font-semibold",
      bold: "ui-font-bold",
      extrabold: "ui-font-extrabold",
      black: "ui-font-black"
    }
  },
  defaultVariants: {
    variant: "base",
    height: "normal",
    weight: "normal",
    font: "sans"
  }
});
function Typography({
  align = "left",
  Element = "p",
  children,
  className,
  color = "dark",
  font = "sans",
  height,
  variant = "base",
  weight = "normal",
  style,
  ...props
}) {
  let _weight = weight;
  if (variant === "h1" || variant === "h2" || variant === "h3" || variant === "h4") {
    _weight = "light";
  }
  return /* @__PURE__ */ jsx11(Element, {
    className: typography({
      align,
      color,
      height,
      variant,
      weight: _weight,
      font,
      className
    }),
    style,
    ...props,
    children
  });
}
Typography.displayName = "Typography";

// src/v3/InfoModal/InfoModalMedia.tsx
import { jsxs as jsxs5 } from "react/jsx-runtime";
function InfoModalMedia({
  image,
  video
}) {
  return /* @__PURE__ */ jsxs5("div", {
    className: "ui-aspect-[5/3] ui-grid ui-grid-cols-1 ui-grid-rows-1 ui-mb-5 ui-w-full ui-shrink-0 ui-rounded-[20px] ui-overflow-hidden md:ui-mb-0",
    children: [
      image,
      video
    ]
  });
}

// src/v3/InfoModal/index.tsx
import { jsx as jsx12, jsxs as jsxs6 } from "react/jsx-runtime";
function InfoModal({
  eyebrow,
  footnote,
  leftColumnContent,
  media,
  rightColumnContent,
  title,
  triggerButton
}) {
  const hasFootnote = Boolean(footnote);
  return /* @__PURE__ */ jsx12(Modal, {
    className: cx5({
      "ui-pb-5": hasFootnote,
      "ui-pb-12": !hasFootnote
    }),
    dialogClassName: "ui-p-0",
    onTriggerButtonClick: triggerButton.onClick,
    triggerButton: (props) => /* @__PURE__ */ jsxs6("button", {
      className: cx5(
        "ui-size-10 ui-relative ui-text-gray-400 hover:ui-text-gray-100",
        "before:ui-absolute before:ui-top-1/2 before:ui-left-1/2 before:-ui-translate-x-1/2 before:-ui-translate-y-1/2 before:ui-size-5 before:ui-bg-gray-300 before:ui-z-0 before:ui-rounded-full hover:before:ui-bg-gray-400",
        triggerButton.className
      ),
      "data-cy": "info-modal-trigger",
      type: "button",
      ...props,
      children: [
        /* @__PURE__ */ jsx12(InfoIcon, {
          className: "ui-absolute ui-top-1/2 ui-left-1/2 -ui-translate-x-1/2 -ui-translate-y-1/2 ui-z-10",
          height: 9,
          width: 4
        }),
        /* @__PURE__ */ jsx12("span", {
          className: "ui-sr-only",
          children: triggerButton.label
        })
      ]
    }),
    variant: "large",
    children: ({ titleProps }) => /* @__PURE__ */ jsxs6("div", {
      className: "ui-flex ui-flex-col ui-flex-wrap md:ui-flex-row",
      "data-cy": "information-modal-content",
      children: [
        /* @__PURE__ */ jsx12("div", {
          className: "ui-mb-4 ui-order-1 ui-w-full md:ui-hidden",
          children: /* @__PURE__ */ jsx12(InfoModalMedia, {
            ...media
          })
        }),
        /* @__PURE__ */ jsxs6("div", {
          className: "ui-w-full ui-mb-4 ui-order-2 md:ui-mb-8",
          children: [
            eyebrow ? /* @__PURE__ */ jsx12("div", {
              className: "ui-mb-1 md:ui-mb-0.5",
              children: eyebrow
            }) : null,
            /* @__PURE__ */ jsx12(Typography, {
              Element: "h2",
              className: "ui-font-normal ui-text-xl ui-tracking-tighter md:ui-text-2xl",
              id: titleProps.id,
              children: title
            })
          ]
        }),
        /* @__PURE__ */ jsxs6("div", {
          className: cx5(
            "ui-w-full ui-order-6 md:ui-order-3 md:ui-w-1/2 md:ui-mr-9",
            {
              "ui-pt-6 ui-border-t ui-border-gray-300 ui-mt-3 md:ui-border-0 md:ui-pt-0 md:ui-mt-0": Boolean(leftColumnContent)
            }
          ),
          children: [
            /* @__PURE__ */ jsx12("div", {
              className: "ui-hidden ui-w-full ui-mb-6 last:ui-mb-0 md:ui-block",
              children: /* @__PURE__ */ jsx12(InfoModalMedia, {
                ...media
              })
            }),
            leftColumnContent
          ]
        }),
        /* @__PURE__ */ jsx12("div", {
          className: "ui-w-full ui-text-sandstone-500 ui-order-4 md:ui-flex-1 md:ui-w-auto",
          children: rightColumnContent
        }),
        footnote ? /* @__PURE__ */ jsx12("div", {
          className: "ui-order-5 ui-w-full ui-pt-3 md:ui-border-t md:ui-border-gray-300 md:ui-pt-5 md:ui-mt-8",
          children: /* @__PURE__ */ jsx12(Typography, {
            className: "ui-text-xs ui-font-normal ui-text-gray-400",
            children: footnote
          })
        }) : null
      ]
    })
  });
}
var InfoModal_default = InfoModal;
export {
  InfoModal,
  InfoModal_default as default
};
//# sourceMappingURL=index.mjs.map